import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import PropertyForm from './components/PropertyForm';
import OwnerInformation from './components/OwnerInformation';
import PhotoUpload from './components/PhotoUpload';
import SubscriptionLimitBanner from './components/SubscriptionLimitBanner';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';

const PropertyCreation = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  const subscriptionTier = userProfile?.subscription_tier || 'free';
  const [currentPropertyCount, setCurrentPropertyCount] = useState(0);
  const [owners, setOwners] = useState([{ name: '', email: '' }]);
  const [photos, setPhotos] = useState([]);
  const [propertyFormData, setPropertyFormData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoadingProperty, setIsLoadingProperty] = useState(false);
  const [initialPropertyData, setInitialPropertyData] = useState(null);

  // Refs for form validation
  const propertyFormRef = useRef(null);
  const ownerInfoRef = useRef(null);

  // Check if we're in edit mode
  const urlParams = new URLSearchParams(window.location.search);
  const editPropertyId = urlParams?.get('edit');
  const isEditMode = !!editPropertyId;

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/agent-dashboard', icon: 'LayoutDashboard' },
    { label: isEditMode ? 'Edit Property' : 'Create Property', icon: isEditMode ? 'Edit' : 'Plus' }
  ];

  // Load existing property data when in edit mode
  useEffect(() => {
    if (isEditMode && editPropertyId && user) {
      loadPropertyData(editPropertyId);
    }
  }, [isEditMode, editPropertyId, user]);

  const loadPropertyData = async (propertyId) => {
    setIsLoadingProperty(true);
    try {
      const { data, error } = await propertyService?.getProperty(propertyId);
      
      if (error) {
        throw new Error(error);
      }

      if (data) {
        // Parse address back into components
        const addressParts = data?.address?.split(',')?.map(part => part?.trim());
        const streetAddress = addressParts?.[0] || '';
        const city = addressParts?.[1] || '';
        const stateZip = addressParts?.[2] || '';
        const stateZipParts = stateZip?.split(' ');
        const state = stateZipParts?.[0] || '';
        const zipCode = stateZipParts?.[1] || '';

        // Set initial property form data
        setInitialPropertyData({
          address: streetAddress,
          city: city,
          state: state,
          zipCode: zipCode,
          friendlyName: '',
          propertyType: data?.property_type || '',
          listingPrice: data?.listing_price?.toString() || '',
          commissionPercentage: data?.commission_percentage?.toString() || ''
        });

        // Set owners if they exist
        if (data?.property_owners && data?.property_owners?.length > 0) {
          setOwners(data?.property_owners?.map(owner => ({
            name: owner?.name || '',
            email: owner?.email || ''
          })));
        }

        // Set photos if image exists
        if (data?.image_url) {
          setPhotos([{
            id: 1,
            url: data?.image_url,
            alt: data?.image_alt || 'Property image',
            file: null
          }]);
        }
      }
    } catch (error) {
      console.error('Error loading property:', error);
      setSaveError('Failed to load property data');
    } finally {
      setIsLoadingProperty(false);
    }
  };

  // Load property count for subscription limit check
  useEffect(() => {
    if (user && subscriptionTier === 'free') {
      loadPropertyCount();
    }
  }, [user, subscriptionTier]);

  const loadPropertyCount = async () => {
    const { data, error } = await propertyService?.getProperties(user?.id);
    if (!error && data) {
      const activeCount = data?.filter(p => p?.is_active)?.length || 0;
      setCurrentPropertyCount(activeCount);
    }
  };

  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (owners?.some(owner => owner?.name || owner?.email) || photos?.length > 0) {
        handleAutoSave();
      }
    }, 30000);

    return () => clearTimeout(autoSaveTimer);
  }, [owners, photos]);

  const handleAutoSave = async () => {
    setIsSaving(true);
    setSaveError(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLastSaved(new Date());
    } catch (error) {
      setSaveError('Auto-save failed');
      console.error('Auto-save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePropertyFormChange = (formData) => {
    setPropertyFormData(formData);
  };

  const handleSaveProperty = async () => {
    console.log('Save property clicked');
    setSaveError(null);

    // Validate forms
    const propertyFormValid = propertyFormRef?.current?.validate();
    const ownerInfoValid = ownerInfoRef?.current?.validate();

    if (!propertyFormValid || !ownerInfoValid) {
      setSaveError('Please fill in all required fields');
      return;
    }

    // Check subscription limit for new properties (not in edit mode)
    if (!isEditMode && subscriptionTier === 'free') {
      const limitCheck = await propertyService?.checkPropertyLimit(user?.id);
      
      if (!limitCheck?.allowed) {
        setSaveError('You have reached the limit of 1 property on the Free plan. Please upgrade to Pro to add more properties.');
        // Scroll to top to show error
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // Get validated data from refs
    const validatedPropertyData = propertyFormRef?.current?.getData();
    const validatedOwners = ownerInfoRef?.current?.getData();

    setIsSaving(true);
    
    try {
      // Upload photos if any
      let imageUrl = null;
      let imageAlt = null;
      
      if (photos?.length > 0) {
        const firstPhoto = photos?.[0];
        // Only upload if it's a new file (not existing URL)
        if (firstPhoto?.file) {
          const { data: uploadData, error: uploadError } = await propertyService?.uploadPropertyPhoto(
            firstPhoto?.file,
            user?.id
          );
          
          if (uploadError) {
            throw new Error(uploadError);
          }
          
          imageUrl = uploadData?.url;
          imageAlt = firstPhoto?.alt || 'Property image';
        } else {
          // Keep existing image
          imageUrl = firstPhoto?.url;
          imageAlt = firstPhoto?.alt;
        }
      }

      // Construct complete address from form fields
      const fullAddress = `${validatedPropertyData?.address}, ${validatedPropertyData?.city}, ${validatedPropertyData?.state} ${validatedPropertyData?.zipCode}`;

      // Use friendly name if provided, otherwise use street address
      const displayName = validatedPropertyData?.friendlyName?.trim() || validatedPropertyData?.address;

      // Remove commas from listing price for database numeric type
      const numericPrice = validatedPropertyData?.listingPrice ? parseFloat(validatedPropertyData?.listingPrice?.toString()?.replace(/,/g, '')) : null;
      const numericCommission = validatedPropertyData?.commissionPercentage ? parseFloat(validatedPropertyData?.commissionPercentage) : null;

      const propertyData = {
        address: fullAddress,
        listingPrice: numericPrice,
        propertyType: validatedPropertyData?.propertyType,
        commissionPercentage: numericCommission,
        imageUrl,
        imageAlt,
        owners: validatedOwners
      };

      let data, error;

      if (isEditMode) {
        // Update existing property
        const updateResult = await propertyService?.updateProperty(editPropertyId, propertyData);
        data = updateResult?.data;
        error = updateResult?.error;

        // Update owners separately
        if (!error) {
          await propertyService?.updatePropertyOwners(editPropertyId, validatedOwners);
        }
      } else {
        // Create new property
        const createResult = await propertyService?.createProperty(user?.id, propertyData);
        data = createResult?.data;
        error = createResult?.error;
      }

      if (error) {
        throw new Error(error);
      }

      setShowSuccessModal(true);
      
      setTimeout(() => {
        navigate('/agent-dashboard');
      }, 2000);
    } catch (error) {
      setSaveError(error?.message || `Failed to ${isEditMode ? 'update' : 'create'} property`);
      console.error('Property save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/agent-dashboard');
    }
  };

  // Only show limit banner/block if creating NEW property (not editing)
  const isAtLimit = !isEditMode && subscriptionTier === 'free' && currentPropertyCount >= 1;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Breadcrumbs items={breadcrumbItems} />

      <main className="main-content">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  {isEditMode ? 'Edit Property' : 'Create New Property'}
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  {isEditMode ? 'Update property details and manage offers' : 'Add property details to start managing offers'}
                </p>
              </div>
              <AutoSaveIndicator 
                isSaving={isSaving} 
                lastSaved={lastSaved} 
                error={saveError}
              />
            </div>

            {!isEditMode && (
              <SubscriptionLimitBanner
                currentCount={currentPropertyCount}
                maxCount={1}
                subscriptionTier={subscriptionTier}
                type="property"
              />
            )}
          </div>

          {isAtLimit ? (
            <div className="bg-card rounded-lg p-6 md:p-8 lg:p-12 text-center shadow-elevation-md">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 rounded-full bg-warning/10 flex items-center justify-center">
                <Icon name="Lock" size={32} className="text-warning" />
              </div>
              <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                Property Limit Reached
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-md mx-auto">
                You've reached the maximum number of properties for the Free plan. 
                Upgrade to Pro to create unlimited properties.
              </p>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              {isLoadingProperty ? (
                <div className="bg-card rounded-lg p-8 text-center shadow-elevation-md">
                  <div className="flex items-center justify-center gap-3">
                    <Icon name="Loader" size={24} className="text-primary animate-spin" />
                    <p className="text-muted-foreground">Loading property data...</p>
                  </div>
                </div>
              ) : (
                <>
                  <PropertyForm 
                    key={editPropertyId || 'new'}
                    ref={propertyFormRef}
                    onChange={handlePropertyFormChange}
                    subscriptionTier={subscriptionTier}
                    initialData={initialPropertyData}
                  />

                  <OwnerInformation 
                    ref={ownerInfoRef}
                    owners={owners}
                    onChange={setOwners}
                  />

                  <PhotoUpload 
                    photos={photos}
                    onChange={setPhotos}
                    maxPhotos={10}
                  />
                </>
              )}

              {saveError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertCircle" size={20} className="text-destructive" />
                    <p className="text-sm text-destructive font-medium">{saveError}</p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-end pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="default"
                  onClick={handleSaveProperty}
                  disabled={isSaving || isLoadingProperty}
                  className="w-full sm:w-auto min-w-[200px]"
                >
                  {isSaving ? (
                    <>
                      <Icon name="Loader" size={20} className="animate-spin" />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <Icon name={isEditMode ? "Save" : "Plus"} size={20} />
                      {isEditMode ? 'Update Property' : 'Create Property'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-6 md:p-8 max-w-md w-full shadow-elevation-lg">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
              <Icon name="CheckCircle" size={32} className="text-success" />
            </div>
            <h3 className="text-xl font-semibold text-foreground text-center mb-2">
              {isEditMode ? 'Property Updated!' : 'Property Created!'}
            </h3>
            <p className="text-sm text-muted-foreground text-center">
              {isEditMode ? 'Your property has been updated successfully.' : 'Your property has been added successfully.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyCreation;